import React from 'react';
import type { ReactNode, RefObject } from 'react';
import {
	Button,
	Container,
	Dropdown,
	Form,
	Nav,
	Navbar,
	Spinner,
	CloseButton,
	Overlay,
	OverlayTrigger,
	Popover,
	Tooltip,
} from 'react-bootstrap';
import {
	BsArrowLeftShort,
	BsFillCaretDownFill,
	BsLayoutTextWindow,
	BsShare,
	BsFiletypeMp4,
	BsFiletypeSvg,
	BsFiletypePdf,
	BsFiletypeAi,
} from 'react-icons/bs';

import BayBadge from '../../BayBadge';
import BayTooltip from '../../BayTooltip';
import BayUserTour, { type UserTourConfig } from '../../BayUserTour';

interface ExportOption {
	type: string;
	label: string;
	icon?: React.ComponentType<any>;
	color?: string;
}

type IconComponent = React.ComponentType<{ size?: number | string; color?: string }>;

interface ImportOption {
	key?: string;
	type?: string;
	label: string;
	accept?: string;
	icon?: IconComponent;
	iconColor?: string;
	description?: string;
	shortLabel?: string;
	beta?: boolean;
}

type ImportOptionFallback = Pick<
	ImportOption,
	'icon' | 'iconColor' | 'description' | 'shortLabel'
>;

/** An import option after the per-type fallbacks have been merged in. */
type ResolvedImportOption = ImportOption & { key: string };

const IMPORT_OPTION_FALLBACKS: Record<string, ImportOptionFallback> = {
	json: {
		shortLabel: 'JSON',
		description:
			'Import an existing BAY design JSON file to continue editing a previously exported design.',
	},
	svg: {
		icon: BsFiletypeSvg,
		iconColor: 'var(--bs-primary)',
		description:
			'Import scalable vector graphics. You can resize them without losing quality and easily customize their colors.',
	},
	pdf: {
		icon: BsFiletypePdf,
		iconColor: 'var(--bs-primary)',
		description:
			'Import multi-page PDF documents. We will extract the text, images, and shapes into editable design layers.',
	},
	ai: {
		icon: BsFiletypeAi,
		iconColor: 'var(--bs-primary)',
		description:
			'Bring your Illustrator files directly into the editor. Your artwork will be converted into fully editable elements.',
	},
};

const resolveImportOptionType = (option: Partial<ImportOption> = {}): string => {
	const byKey = String(option?.key || option?.type || '')
		.toLowerCase()
		.trim();
	if (byKey && IMPORT_OPTION_FALLBACKS[byKey]) return byKey;

	const label = String(option?.label || '').toLowerCase();
	if (label.includes('illustrator') || label.includes(' ai')) return 'ai';
	if (label.includes('svg')) return 'svg';
	if (label.includes('pdf')) return 'pdf';
	if (label.includes('json')) return 'json';

	const accept = String(option?.accept || '').toLowerCase();
	if (accept.includes('.ai')) return 'ai';
	if (accept.includes('.svg')) return 'svg';
	if (accept.includes('.pdf')) return 'pdf';
	if (accept.includes('.json')) return 'json';

	return '';
};

interface EditorHeaderProps {
	// Navigation
	onBack?: () => void;
	editorTitle?: string;

	// Project state
	projectName?: string;
	projectNameInputRef?: RefObject<HTMLInputElement>;
	onNameChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	projectNameError?: boolean;

	// Status (optional - for design editor)
	status?: string;
	showStatus?: boolean;

	// Actions
	onSave?: (shareToSocial: boolean) => void;
	isSaving?: boolean;
	isLoading?: boolean;
	saveDisabled?: boolean;
	saveDisabledTooltipContent?: ReactNode;
	overlayMaxWidth?: number | string;

	// Export
	exportOptions?: ExportOption[];
	onExport?: (type: string) => void;
	showVideoExportButton?: boolean;
	onExportVideo?: () => void;
	isVideoExporting?: boolean;

	// Close/Exit
	showCloseButton?: boolean;
	onClose?: () => void;

	// Access & Admin
	access?: any; // TODO: Create proper access type
	isDesignAdmin?: boolean;

	// Preview & Test button
	showPreviewAndTestButton?: boolean;
	onPreviewAndTest?: () => void;
	blockMode?: boolean;

	// Template error state (must be provided by caller if inside an email editor store)
	hasTemplateError?: boolean;

	// Additional content
	shareButtonVariant?: 'compact' | 'standard';
	showShareToSocialButton?: boolean;
	shareToSocialDisabled?: boolean;
	shareToSocialTooltip?: string;
	children?: ReactNode;
	showImportButton?: boolean;
	importOptions?: ImportOption[];
	onImport?: (option: ResolvedImportOption) => void;
	importDisabled?: boolean;

	// Omitted -> BayUserTour falls back to window.BAY_USERTOUR
	userTour?: UserTourConfig;

	// Translation
	t?: (key: string, options?: any) => string;
}

const CustomToggle = React.forwardRef<HTMLButtonElement, {
	children: ReactNode;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	size?: 'sm' | 'lg';
	disabled?: boolean;
	variant?: string;
		}>(({ children, onClick, size = 'sm', disabled = false, variant = 'outline-primary' }, ref) => (
			<Button
				ref={ref}
				className="me-3 d-flex align-items-center"
				disabled={disabled}
				size={size}
				variant={variant}
				onClick={(e) => {
					e.preventDefault();
					if (onClick) {
						onClick(e);
					}
				}}
			>
				{children}
				<span className="ms-2">
					<BsFillCaretDownFill />
				</span>
			</Button>
		));

CustomToggle.displayName = 'CustomToggle';

const ExportDropdown: React.FC<{
	exportOptions: ExportOption[];
	onExport: (type: string) => void;
	disabled: boolean;
	t: (key: string) => string;
}> = ({ exportOptions, onExport, disabled, t }) => {
	if (!exportOptions?.length) return null;

	return (
		<Dropdown align="end">
			<Dropdown.Toggle as={CustomToggle} disabled={disabled}>
				{t('download')}
			</Dropdown.Toggle>
			<Dropdown.Menu className="bay-editor-header-dropdown-menu">
				{exportOptions.map((option, index) => (
					<Dropdown.Item
						key={index}
						className="text-decoration-none"
						onClick={() => onExport(option.type)}
					>
						{option.icon && (
							<span className="me-2">
								<option.icon color={option.color} size={16} />
							</span>
						)}
						{option.label}
					</Dropdown.Item>
				))}
			</Dropdown.Menu>
		</Dropdown>
	);
};

const ImportDropdown: React.FC<{
	importOptions?: ImportOption[];
	onImport?: (option: ResolvedImportOption) => void;
	disabled?: boolean;
}> = ({ importOptions, onImport, disabled }) => {
	if (!importOptions?.length || typeof onImport !== 'function') return null;

	return (
		<Dropdown align="start" className="ms-2">
			<Dropdown.Toggle as={CustomToggle} disabled={disabled} variant="primary">
				Import
			</Dropdown.Toggle>
			<Dropdown.Menu
				className="bay-editor-header-dropdown-menu bay-import-menu"
				style={{ width: 'min(500px, calc(100vw - 2rem))', minWidth: '22rem' }}
			>
				{importOptions.map((option, index) => {
					const optionType = resolveImportOptionType(option);
					const fallback: ImportOptionFallback = IMPORT_OPTION_FALLBACKS[optionType] || {};
					const resolvedOption: ResolvedImportOption = {
						...fallback,
						...option,
						key: option?.key || optionType || `import-${index}`,
						icon: option?.icon || fallback.icon,
						iconColor: option?.iconColor || fallback.iconColor,
						description: option?.description || fallback.description,
						shortLabel: option?.shortLabel || fallback.shortLabel,
					};
					const OptionIcon = resolvedOption.icon;

					return (
						<React.Fragment key={resolvedOption.key || index}>
							<Dropdown.Item
								className="text-wrap"
								onClick={() => onImport?.(resolvedOption)}
							>
								<div className="bay-import-option-card d-flex align-items-start gap-3">
									<div
										className="bg-primary-subtle text-primary rounded-1 d-flex align-items-center justify-content-center fw-semibold"
										style={{
											width: '2.75rem',
											height: '2.75rem',
											minWidth: '2.75rem',
										}}
									>
										{OptionIcon ? (
											<OptionIcon
												size={22}
												color={resolvedOption.iconColor || 'var(--bs-primary)'}
											/>
										) : (
											resolvedOption.shortLabel || resolvedOption.label
										)}
									</div>

									<div className="flex-grow-1">
										<div className="d-flex align-items-center gap-2 mb-1">
											<span className="fw-semibold text-dark">
												{resolvedOption.label}
											</span>
											{resolvedOption.beta && (
												<BayBadge isLight bg="indigo" text="indigo">
													Beta
												</BayBadge>
											)}
										</div>
										{resolvedOption.description && (
											<div className="text-muted small lh-sm">
												{resolvedOption.description}
											</div>
										)}
									</div>
								</div>
							</Dropdown.Item>

							{index < importOptions.length - 1 && (
								<div className="bay-import-option-divider border-bottom"></div>
							)}
						</React.Fragment>
					);
				})}
			</Dropdown.Menu>
		</Dropdown>
	);
};

const ShareButton: React.FC<{
	t: (key: string) => string;
	onExport: (type: string) => void;
	exportOptions: ExportOption[];
	isSaving: boolean;
	isVideoExporting: boolean;
	isLoading: boolean;
	projectNameError: boolean;
	shareButtonVariant?: 'compact' | 'standard';
	isDesignAdmin: boolean;
	showShareToSocialButton: boolean;
	onSave: (shareToSocial: boolean) => void;
	shareToSocialDisabled: boolean;
	shareToSocialTooltip: string;
}> = ({
	t,
	onExport,
	exportOptions,
	isSaving,
	isVideoExporting,
	isLoading,
	projectNameError,
	shareButtonVariant,
	isDesignAdmin,
	showShareToSocialButton,
	onSave,
	shareToSocialDisabled,
	shareToSocialTooltip,
}) => {
	const shareDisabled = Boolean(
		isSaving ||
		isVideoExporting ||
		isLoading ||
		projectNameError ||
		shareToSocialDisabled,
	);

	const renderShareMenuItem = () => {
		const menuItem = (
			<Dropdown.Item
				className="text-decoration-none"
				disabled={shareDisabled}
				eventKey="1"
				onClick={() => onSave(true)}
			>
				{t('shareToSocial')}
			</Dropdown.Item>
		);

		if (!shareToSocialDisabled || !shareToSocialTooltip) {
			return menuItem;
		}

		return (
			<OverlayTrigger
				overlay={(props) => (
					<Tooltip id="share-to-social-menu-tooltip" {...props}>
						{shareToSocialTooltip}
					</Tooltip>
				)}
				placement="bottom"
			>
				<span className="d-block">{menuItem}</span>
			</OverlayTrigger>
		);
	};

	const nonCompactShareButton = (
		<Button
			className="me-3"
			disabled={shareDisabled}
			size="sm"
			variant="primary"
			onClick={() => onSave(true)}
		>
			{isSaving ? (
				<span className="d-flex align-items-center">
					<Spinner className="me-2" size="sm" />
					<span>{t('processing')}</span>
				</span>
			) : (
				<span className="d-flex align-items-center">
					<BsShare className="me-2 " />
					<span>{t('shareToSocial')}</span>
				</span>
			)}
		</Button>
	);

	const exportMenu = (
		<Dropdown.Menu className="bay-editor-header-dropdown-menu">
			{exportOptions.map((option, index) => (
				<Dropdown.Item
					key={index}
					className="text-decoration-none"
					eventKey={index.toString()}
					onClick={() => onExport(option.type)}
				>
					{option.icon && (
						<span className="me-2">
							<option.icon color={option.color} size={16} />
						</span>
					)}
					{option.label}
				</Dropdown.Item>
			))}
		</Dropdown.Menu>
	);

	// Determine whether to show share button
	if (!showShareToSocialButton || isDesignAdmin) {
		return null;
	}

	// Compact mode: Render Share button with Download dropdown inside
	if (shareButtonVariant === 'compact') {
		return (
			<>
				{!isDesignAdmin ? (
					<Dropdown align="end">
						<Dropdown.Toggle as={CustomToggle} disabled={shareDisabled}>
							{t('share')}
						</Dropdown.Toggle>

						<Dropdown.Menu className="bay-editor-header-dropdown-menu">
							{renderShareMenuItem()}

							<Dropdown align="end" className="dropdown-item position-relative">
								<Dropdown.Toggle as="span" className="w-100 d-inline-block">
									{t('download')}
								</Dropdown.Toggle>
								{exportMenu}
							</Dropdown>
						</Dropdown.Menu>
					</Dropdown>
				) : (
					false
				)}
			</>
		);
	}

	// Non-compact mode: Only render the Share button
	// The Download dropdown is handled by the separate ExportDropdown component
	return (
		<>
			{!isDesignAdmin &&
				(shareToSocialDisabled && shareToSocialTooltip ? (
					<OverlayTrigger
						overlay={(props) => (
							<Tooltip id="share-to-social-button-tooltip" {...props}>
								{shareToSocialTooltip}
							</Tooltip>
						)}
						placement="bottom"
					>
						<span className="d-inline-block">{nonCompactShareButton}</span>
					</OverlayTrigger>
				) : (
					nonCompactShareButton
				))}
		</>
	);
};

const EditorHeader: React.FC<EditorHeaderProps> = ({
	// Navigation
	onBack,
	editorTitle = 'editor',

	// Project state
	projectName,
	projectNameInputRef,
	onNameChange,
	projectNameError,

	// Status (optional - for design editor)
	status,
	showStatus = false,

	// Actions
	onSave = () => {},
	isSaving = false,
	isLoading = false,
	saveDisabled = false,
	saveDisabledTooltipContent,
	overlayMaxWidth = 480,

	// Export
	exportOptions = [],
	onExport = () => {},
	onExportVideo,
	showVideoExportButton = false,
	isVideoExporting = false,

	// Close/Exit
	showCloseButton = false,
	onClose,

	// Access & Admin
	access = {},
	isDesignAdmin = false,

	// Preview & Test button
	showPreviewAndTestButton,
	onPreviewAndTest,
	// Block editor variant: blocks aren't sendable, so the button is preview-only
	// ("Preview", not "Preview & Test").
	blockMode = false,

	// Template error state (must be provided by caller if inside an email editor store)
	hasTemplateError = false,

	// Additional content
	shareButtonVariant,
	showShareToSocialButton = true, // default to true if undefined
	shareToSocialDisabled = false,
	shareToSocialTooltip = '',
	children,
	showImportButton = false,
	importOptions = [],
	onImport,
	importDisabled = false,

	userTour,

	// Translation
	t = (key) => key,
}) => {
	const isSaveButtonDisabled =
		isSaving || isLoading || projectNameError || saveDisabled || hasTemplateError;
	const exportDropdownDisabled = Boolean(
		isSaving || isVideoExporting || isLoading || projectNameError || hasTemplateError,
	);
	const hasMp4Export = Boolean(showVideoExportButton && onExportVideo);
	const resolvedExportOptions: ExportOption[] = hasMp4Export
		? [
			...exportOptions,
			{
				type: 'mp4',
				label: 'MP4	(Video)',
				icon: BsFiletypeMp4,
				color: 'var(--bs-info)',
			},
		]
		: exportOptions;

	const handleExportAction = (exportType: string): void => {
		if (exportType === 'mp4') {
			onExportVideo?.();
			return;
		}

		onExport?.(exportType);
	};

	const getStatusClass = (status: string): string => {
		const lowerCaseStatus = status.toLowerCase();
		const statusMap: Record<string, string> = {
			active: 'teal',
			inactive: 'gray',
			draft: 'yellow',
			review: 'cyan',
			archived: 'red',
		};
		return statusMap[lowerCaseStatus] || 'gray';
	};

	const defaultSaveDisabledOverlay = (
		<div className="bay-tooltip-content">
			<div className="bay-tooltip-title mb-2">
				<strong>{t('overlaySaveTitle')}</strong>
			</div>
			<div className="bay-tooltip-body">
				<p className="mb-2">{t('overlaySaveBody')}</p>
				<ul className="mt-2 mb-0 ps-3">
					<li>
						{t('overlaySaveReqStart')}{' '}
						<strong>{t('overlaySaveReqConnected')}</strong>{' '}
						{t('overlaySaveReqEnd')}
					</li>
				</ul>
			</div>
		</div>
	);

	const resolvedSaveDisabledOverlay: ReactNode =
		saveDisabledTooltipContent || defaultSaveDisabledOverlay;
	const shouldShowSaveDisabledTooltip =
		saveDisabled && (isDesignAdmin || Boolean(saveDisabledTooltipContent));

	return (
		<Navbar
			bg="white"
			className="py-3 border-bottom border-bottom-3"
			expand="lg"
			style={{ height: 60 }}
		>
			<BayUserTour config={userTour} />
			<Container fluid>
				{/* Left: Navigation */}
				<Nav className="col-4 nav-bar-container">
					<div className="d-flex align-items-center">
						<Nav.Item
							as={Button}
							className="text-secondary p-0 text-decoration-none d-flex align-items-center justify-content-between"
							disabled={isSaving || isLoading}
							variant="link"
							onClick={onBack}
						>
							<BsArrowLeftShort className="me-1" size={24} />
							{t('back')}
						</Nav.Item>
						<span className="mx-2 text-200 border-start border-2 border-200" style={{ height: 24, width: 1 }} />
						<span className="text-dark fw-bold fs-5 lh-1">{editorTitle}</span>

						{/* Admin badges */}
						{(access?.isAdmin || access?.isGlobalRole) && isDesignAdmin && (
							<div className="ms-2 ps-1 fw-bold d-flex align-items-center">
								<BayBadge
									isLight={!access?.isSuperAdmin}
									isDark={access?.isSuperAdmin}
									bg={
										access?.isSystemAdmin
											? 'yellow'
											: access?.isSuperAdmin
												? undefined
												: 'pink'
									}
									text={
										access?.isSystemAdmin
											? 'yellow'
											: access?.isSuperAdmin
												? undefined
												: 'pink'
									}
									textValue={
										access?.isSystemAdmin || access?.isSuperAdmin
											? undefined
											: '700'
									}
									className={access?.isSuperAdmin ? 'text-white' : ''}
								>
									{access?.isSystemAdmin
										? t('systemAdmin', { defaultValue: 'System Admin' })
										: access?.isSuperAdmin
											? t('globalAdmin', { defaultValue: 'Global Admin' })
											: t('admin', { defaultValue: 'Admin' })}
								</BayBadge>
							</div>
						)}

						{showImportButton && (
							<ImportDropdown
								importOptions={importOptions}
								onImport={onImport}
								disabled={importDisabled}
							/>
						)}
					</div>
				</Nav>

				{/* Center: Project Name & Status */}
				<Form className="col-3">
					<div className="d-flex align-items-center">
						{/* Status Badge (Design Editor only) */}
						{showStatus && status && (
							<div>
								<BayBadge
									isLight
									bg={getStatusClass(status)}
									text={getStatusClass(status)}
								>
									{t(status)}
								</BayBadge>
							</div>
						)}

						{/* Project Name Input */}
						<Form.Control
							ref={projectNameInputRef}
							className={`mx-2${projectNameError ? ' is-invalid' : ''}`}
							disabled={isSaving || isLoading}
							readOnly={isSaving}
							size="sm"
							type="text"
							value={projectName}
							onChange={onNameChange}
						/>

						{/* Project Name Error Overlay */}
						<Overlay
							flip
							placement="bottom"
							rootClose
							show={projectNameError}
							target={projectNameInputRef?.current}
						>
							{(props) => (
								<Popover {...props} className="project-name-error-overlay">
									<div className="alert alert-danger m-2" role="alert">
										<ul className="m-0">
											<li>Must have a minimum of 3 characters.</li>
											<li>Must be less than a maximum of 128 characters.</li>
											<li>{'Must not contain + = | * ? / \\ < > $'}</li>
											<li>Must not start or end with .</li>
											<li>Must not start or end with a space</li>
											<li>Must not contain double spaces</li>
										</ul>
									</div>
								</Popover>
							)}
						</Overlay>

						{/* Save Button */}
						<BayTooltip
							disabled={!shouldShowSaveDisabledTooltip}
							overlayClassName="listing-overlay-tooltip"
							overlayMaxWidth={overlayMaxWidth}
							placement="bottom"
							overlay={resolvedSaveDisabledOverlay}
						>
							<span className="d-inline-block">
								<Button
									disabled={isSaveButtonDisabled}
									size="sm"
									style={saveDisabled ? { pointerEvents: 'none' } : undefined}
									variant="outline-primary"
									onClick={() => onSave(false)}
								>
									{isSaving ? (
										<span className="d-flex align-items-center">
											<Spinner className="me-2" size="sm" />
											<span>{t('saving')}</span>
										</span>
									) : (
										t('save')
									)}
								</Button>
							</span>
						</BayTooltip>
					</div>
				</Form>

				{/* Right: Actions */}
				<Nav className="col-5 flex-row align-items-center justify-content-end">
					{children}

					{showPreviewAndTestButton && (
						<Button
							variant="outline-primary"
							className="me-3"
							size="sm"
							disabled={isSaving || isLoading || projectNameError || hasTemplateError}
							onClick={onPreviewAndTest}
						>
							<BsLayoutTextWindow className="me-2" />
							{blockMode ? t('preview') : t('previewAndTest')}
						</Button>
					)}

					{/* Export Dropdown - Only show when NOT using compact share button */}
					{shareButtonVariant !== 'compact' && (
						<ExportDropdown
							exportOptions={resolvedExportOptions}
							onExport={handleExportAction}
							disabled={exportDropdownDisabled}
							t={t}
						/>
					)}

					{/* Share Button */}
					<ShareButton
						onExport={handleExportAction}
						exportOptions={resolvedExportOptions}
						onSave={onSave}
						isDesignAdmin={isDesignAdmin}
						isLoading={isLoading}
						isSaving={isSaving}
						isVideoExporting={isVideoExporting}
						projectNameError={projectNameError || false}
						shareButtonVariant={shareButtonVariant}
						showShareToSocialButton={showShareToSocialButton}
						shareToSocialDisabled={shareToSocialDisabled}
						shareToSocialTooltip={shareToSocialTooltip}
						t={t}
					/>

					{/* Close Button */}
					{showCloseButton && (
						<CloseButton
							className="btn-sm text-dark opacity-100"
							disabled={isSaving || isLoading}
							onClick={onClose}
						/>
					)}
				</Nav>
			</Container>
		</Navbar>
	);
};

export default EditorHeader;
